import React, { useState, useEffect } from "react";
import { Box, Paper, Typography, IconButton, Collapse, Slide } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsIcon from "@mui/icons-material/Notifications";

const MAX_TITLE_LENGTH = 50;
const MAX_BODY_LENGTH = 120;

const truncate = (str, maxLen) => {
  if (!str || str.length <= maxLen) return str;
  return str.slice(0, maxLen).trim() + "...";
};

const NotificationItem = ({ notification, onClose, index, expanded, onToggleExpand }) => {
  const hasDeepLink = notification.deepLink && String(notification.deepLink).trim();
  const titleDisplay = truncate(notification.title, MAX_TITLE_LENGTH);
  const bodyDisplay = truncate(notification.body, MAX_BODY_LENGTH);

  const handlePaperClick = (e) => {
    if (hasDeepLink && !e.target.closest("button")) {
      window.open(notification.deepLink.trim(), "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Paper
      elevation={3}
      onClick={handlePaperClick}
      sx={{
        width: 350,
        backgroundColor: "rgba(156, 195, 233, 1)",
        border: "1px solid rgba(76, 143, 209, 0.3)",
        position: "relative",
        cursor: hasDeepLink ? "pointer" : "default",
      }}
    >
      <Box sx={{ p: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: expanded ? 0.5 : 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <NotificationsIcon sx={{ color: "primary.main", fontSize: 20, flexShrink: 0 }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "primary.main", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={notification.title}
            >
              {titleDisplay}
            </Typography>
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>
            <IconButton size="small" onClick={() => onToggleExpand(notification.id)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <IconButton size="small" onClick={() => onClose(notification.id)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            {notification.image && (
              <img
                src={notification.image}
                alt=""
                style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                flex: 1,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
              title={notification.body}
            >
              {bodyDisplay}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

const NotificationManager = ({ notifications, onClose }) => {
    const [expandedId, setExpandedId] = useState(null);
    const [prevLength, setPrevLength] = useState(0);

    useEffect(() => {
        if (notifications.length > prevLength) {
            const newNotification = notifications[notifications.length - 1];
            setExpandedId(newNotification.id);
        }
        setPrevLength(notifications.length);
    }, [notifications.length]);

    const handleToggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    if (notifications.length === 0) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                bottom: 20,
                right: 20,
                zIndex: 9999,
                display: "flex",
                flexDirection: "column-reverse",
                gap: 1,
            }}
        >
            {notifications.map((notification, index) => (
                <Slide
                    key={notification.id}
                    direction="left"
                    in={true}
                    timeout={300}
                >
                    <div>
                        <NotificationItem
                            notification={notification}
                            onClose={onClose}
                            index={index}
                            expanded={expandedId === notification.id}
                            onToggleExpand={handleToggleExpand}
                        />
                    </div>
                </Slide>
            ))}
        </Box>
    );
};

export default NotificationManager;
